<template>
  <CommonsBuyee :input-tag-value="buyeeInputTagValue" />

  <!-- フォーカスがカルーセル内にある間は自動送りを止める（WCAG 2.2.2）。
       `pauseOnMouseEnter` はマウスにしか効かず、キーボード利用者はスライドを読んでいる最中に
       切り替わってしまうため、`focusin` / `focusout` で同じことをする -->
  <section class="sec_home-fv" @focusin="handleFocusIn" @focusout="handleFocusOut">
    <div ref="swiperEl" class="home-fv-inner swiper">
      <div class="swiper-wrapper">
        <div v-for="image in slides" :key="image.src" class="home-fv-img swiper-slide">
          <PartsChromeLink :to="image.link">
            <img :src="image.src" :alt="image.alt" />
          </PartsChromeLink>
        </div>
      </div>
    </div>

    <div ref="paginationEl" class="home-fv-pagination"></div>
  </section>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `sec_home-fv`（FVカルーセル）。
 *
 * 支給は jQuery ＋ slick（`script_main.js:4-15`）。jQuery と slick は持ち込まない（決定#11）ため
 * Swiper で置き換える。支給の slick 設定はそのまま移せる
 * （`centerMode` → `centeredSlides`、`variableWidth` → `slidesPerView: 'auto'`、`infinite` → `loop`）。
 *
 * `<el-carousel>` は使わない。スライドを絶対配置し、動かすのを「消える1枚」と「出てくる1枚」に
 * 限定する作りのため、左右のチラ見せが滑らず瞬間移動してしまう。
 * Swiper は slick と同じくトラックごと動かすので、中央と左右が同時に滑る。
 *
 * バナーは支給素材5枚（PC 1200×668 / SP 750×750）。支給HTMLの画像割り当てのずれを正した対応で並べる
 * （→ `MainCarouselConst`）。飛び先が未確定の枠は `'#'` が入るので、`NuxtLink` ではなく
 * `PartsChromeLink` を通して素の `<a>` に落とす。
 * 高さは指定しない。Swiper はスライドをトラックに横並びにするので、画像の縦横比のまま出る。
 *
 * **操作ボタンは置かない**（2026-08-10 の指示）。出すのはドット（ページネーション）だけで、
 * 一時停止／再生トグルと前後送りボタンは PR-D レビュー C-3 で一度入れたが取り下げた。
 *
 * 画面に出ないぶんのアクセシビリティ対応だけ残す。
 * ①`prefers-reduced-motion: reduce` なら自動送りをしない ②フォーカスが中にある間は止める
 * ③矢印キーでの送り（`Keyboard` モジュール）④ドットのフォーカス可能化と読み上げ文言（`A11y` モジュール）。
 *
 * **WCAG 2.2.2（一時停止・停止・非表示）は満たしていない。** 自動送りは `delay: 2500` ＋
 * `speed: 2000` の 4.5 秒周期で 5 秒を超えて動き続けるが、マウス利用者が明示的に止める手段が無い
 * （`pauseOnMouseEnter` のホバー停止のみ）。ボタンを置かない判断とセットで受け入れている。
 */
import type SwiperClass from 'swiper';
import type { MainCarouselType } from '@/types/domains/MainCarouselType';
import { MAIN_CAROUSEL_PC_IMAGES, MAIN_CAROUSEL_SP_IMAGES } from '@/consts/MainCarouselConst';
import 'swiper/css';
import 'swiper/css/pagination';
// `A11y` が差し込む読み上げ用の `.swiper-notification` を隠すためだけのCSS。
// 入れないと読み上げ文言が画面に見えてしまう
import 'swiper/css/a11y';

const { isLargeScreen } = useDeviceCheck();

const buyeeInputTagValue: string = 'to-page';

const slides = computed<MainCarouselType[]>(() =>
  isLargeScreen.value ? MAIN_CAROUSEL_PC_IMAGES : MAIN_CAROUSEL_SP_IMAGES
);

const swiperEl = ref<HTMLElement | null>(null);
const paginationEl = ref<HTMLElement | null>(null);
let swiper: SwiperClass | null = null;

/**
 * 自動送りをしてよいか。`prefers-reduced-motion: reduce` のときだけ false になる。
 * フォーカスによる一時停止と混ざらないよう別に持つ（フォーカスが外れても再開させないため）
 */
const isAutoplayAllowed = ref<boolean>(true);

/**
 * 自動再生の開始／停止。
 * `pause()` / `resume()` ではなく `start()` / `stop()` を使う。
 * `pause()` は待ち時間を持ち越すために `transitionend` で自動的に `resume()` へ戻る作りで、
 * 遷移中（`speed: 2000` の2秒間）に呼ぶと勝手に再開してしまい「止まらない」ことがあるため。
 */
const startAutoplay = (): void => {
  if (swiper && !swiper.autoplay.running) swiper.autoplay.start();
};
const stopAutoplay = (): void => {
  if (swiper && swiper.autoplay.running) swiper.autoplay.stop();
};

// フォーカスが入っている間は自動送りを止める。読んでいる最中に切り替わらないようにするため
const handleFocusIn = (): void => {
  if (isAutoplayAllowed.value) stopAutoplay();
};

const handleFocusOut = (event: FocusEvent): void => {
  // カルーセル内での移動（矢印→ドット→リンク）では止めたままにする。
  // `currentTarget` はハンドラを張った `<section>`
  const container = event.currentTarget;
  const movedTo = event.relatedTarget;
  if (container instanceof Node && movedTo instanceof Node && container.contains(movedTo)) return;
  if (isAutoplayAllowed.value) startAutoplay();
};

// Swiper 本体はクライアントでだけ読む。SSR では素の `swiper-wrapper` が出るので初期表示は落ちない
onMounted(async () => {
  if (!swiperEl.value || !paginationEl.value) return;

  const [{ default: Swiper }, { A11y, Autoplay, Keyboard, Pagination }] = await Promise.all([
    import('swiper'),
    import('swiper/modules'),
  ]);

  swiper = new Swiper(swiperEl.value, {
    modules: [A11y, Autoplay, Keyboard, Pagination],
    // 支給 `variableWidth: true` に対応。スライド幅はCSS側（1200px）が決める
    slidesPerView: 'auto',
    centeredSlides: true,
    // 支給 `.slick-slide { margin: 0 14px }` の左右14px＝スライド間28px
    spaceBetween: 28,
    loop: true,
    speed: 2000,
    autoplay: { delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true },
    pagination: { el: paginationEl.value, clickable: true },
    // 矢印キーでスライドを送れるようにする（WCAG 2.1.1）
    keyboard: { enabled: true, onlyInViewport: true },
    // ドットをフォーカス可能にし、読み上げ文言を日本語にする
    a11y: {
      enabled: true,
      // `aria-label` / `aria-roledescription` は role の無い `<div>` には効かないので
      // 併せて `role="region"` を付ける（ARIA APG のカルーセルパターン）
      containerRole: 'region',
      containerMessage: 'メインビジュアル',
      containerRoleDescriptionMessage: 'カルーセル',
      prevSlideMessage: '前のスライドへ',
      nextSlideMessage: '次のスライドへ',
      paginationBulletMessage: '{{index}} 枚目のスライドへ',
      slideLabelMessage: '{{index}} / {{slidesLength}} 枚目',
    },
  });

  // 動きを減らす設定のユーザーには自動送りをしない
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    isAutoplayAllowed.value = false;
    stopAutoplay();
  }
});

// PC幅とSP幅でスライドの画像が入れ替わるので、差し替え後に再計測させる
watch(slides, async () => {
  await nextTick();
  swiper?.update();
});

onBeforeUnmount(() => {
  swiper?.destroy();
  swiper = null;
});
</script>

<style scoped>
/* スライドは 1200px 固定・左右はビューポート端まで見せる。
   切るのは Swiper 自身の `.swiper { overflow: hidden }` で、支給の `.slick-list` と同じ役割 */
.home-fv-inner {
  width: 100%;
}

.home-fv-img {
  width: 1200px;
  max-width: 100vw;
  height: auto;
}

.home-fv-img a {
  display: block;
}

.home-fv-img img {
  display: block;
  width: 100%;
  height: auto;
}

/* 支給の `.slick-dots` に寄せる。
   ・大きさ … 支給は `li button:before { font-size: 9px !important }`（`style.css:1205-1207`）で、
     slick 既定の 6px を 1.5 倍に上書きしている。字形は slick 同梱のアイコンフォントの `•` で
     em をほぼ埋めるため、実寸はフォントサイズとほぼ同じ。したがって 9px を採る
   ・間隔 … 支給は `li { margin: 0 10px }` ＋ slick 既定の `li { width: 20px }` でピッチ 40px。
     こちらは丸 9px なので左右 15.5px でピッチ 40px に合わせる
   ・位置 … 支給 `bottom: -60px` ＝ スライダー下 40px
   ・色 … アクティブは smoky-red、非アクティブは slick 既定の `opacity: .25` */
.home-fv-pagination {
  --swiper-pagination-bullet-width: 9px;
  --swiper-pagination-bullet-height: 9px;
  --swiper-pagination-bullet-horizontal-gap: 15.5px;
  --swiper-pagination-bullet-inactive-color: var(--text-gray);
  --swiper-pagination-bullet-inactive-opacity: 0.25;
  --swiper-pagination-color: var(--smoky-red);
  margin-top: 40px;
  text-align: center;
}
</style>
